import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, HelpCircle, Menu, ChevronDown, LogOut, User as UserIcon, Settings, Sun, Moon, WifiOff } from 'lucide-react';
import axios from 'axios';
import { API } from '../../App';
import AwesomeBar from './AwesomeBar';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { useTranslation } from '../../i18n';

export default function ERPNextTopbar({ user, setUser, onToggleSidebar }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [theme, setTheme] = useState(() => localStorage.getItem('erp_theme') || 'light');
  const menuRef = useRef(null);
  const bellRef = useRef(null);

  // Apply theme
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('erp_theme', theme);
  }, [theme]);

  // Fetch unread notifications
  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const r = await axios.get(`${API}/notifications`, { params: { read: false, limit: 10 } });
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data : (r.data?.notifications || []);
        setNotifications(list);
        setUnread(list.filter(n => !n.read).length);
      } catch (_) { /* notifications endpoint may be in transition */ }
    };
    fetch();
    const t = setInterval(fetch, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Outside-click handlers
  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (_) {}
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser?.(null);
    navigate('/login');
  };

  const online = useOnlineStatus();
  const { t, locale, setLocale, supported, labels } = useTranslation();
  const initials = (user?.full_name || user?.email || 'U').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();

  return (
    <header className="erp-topbar">
      <button
        className="erp-icon-btn"
        onClick={onToggleSidebar}
        aria-label="Toggle sidebar"
        style={{ marginRight: 4 }}
      >
        <Menu size={18} />
      </button>

      <AwesomeBar />

      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
        {!online && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, padding: '3px 8px', background: 'var(--erp-warning, #f0a74e)', color: 'white', borderRadius: 999 }}>
            <WifiOff size={11} /> {t('Offline — changes queued')}
          </span>
        )}
        <select
          value={locale}
          onChange={e => setLocale(e.target.value)}
          aria-label={t('Language')}
          title={t('Language')}
          style={{ fontSize: 12, padding: '3px 6px', border: '1px solid var(--erp-border)', borderRadius: 5, background: 'var(--erp-surface)', color: 'var(--erp-text)' }}
        >
          {supported.map(l => (
            <option key={l} value={l}>{labels[l] || l.toUpperCase()}</option>
          ))}
        </select>

        <button
          className="erp-icon-btn"
          onClick={() => setTheme(prev => prev === 'light' ? 'dark' : 'light')}
          aria-label={t('Toggle theme')}
          title={theme === 'light' ? t('Switch to dark') : t('Switch to light')}
        >
          {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
        </button>

        <a className="erp-icon-btn" href="/help" aria-label="Help">
          <HelpCircle size={18} />
        </a>

        <div ref={bellRef} style={{ position: 'relative' }}>
          <button className="erp-icon-btn" onClick={() => setBellOpen(o => !o)} aria-label={`Notifications (${unread} unread)`}>
            <Bell size={18} />
            {unread > 0 && <span className="badge">{unread > 99 ? '99+' : unread}</span>}
          </button>
          {bellOpen && (
            <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', width: 360, maxHeight: 480, overflowY: 'auto', background: 'var(--erp-surface)', border: '1px solid var(--erp-border)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.08)', zIndex: 100 }}>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--erp-border)', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Notifications</span>
                <button
                  className="erp-btn"
                  style={{ padding: '2px 8px', fontSize: 11 }}
                  onClick={async () => {
                    try { await axios.post(`${API}/notifications/mark-all-read`); setNotifications(n => n.map(x => ({ ...x, read: true }))); setUnread(0); }
                    catch (_) {}
                  }}
                >Mark all read</button>
              </div>
              {notifications.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--erp-text-muted)', fontSize: 13 }}>No notifications</div>
              ) : (
                notifications.map((n, i) => (
                  <div
                    key={n.id || i}
                    onClick={() => { if (n.link) navigate(n.link); setBellOpen(false); }}
                    style={{ padding: '10px 14px', borderBottom: '1px solid var(--erp-border)', cursor: 'pointer', fontSize: 13, background: n.read ? 'transparent' : 'var(--erp-surface-2)' }}
                  >
                    <div style={{ fontWeight: 500, color: 'var(--erp-text)' }}>{n.title || n.subject || 'Notification'}</div>
                    {n.body && <div style={{ color: 'var(--erp-text-muted)', fontSize: 12, marginTop: 2 }}>{n.body}</div>}
                    <div style={{ color: 'var(--erp-text-faint)', fontSize: 11, marginTop: 4 }}>{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            className="erp-icon-btn"
            onClick={() => setMenuOpen(o => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px' }}
          >
            <span style={{ width: 26, height: 26, background: 'var(--erp-primary)', color: 'white', borderRadius: 13, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600 }}>{initials}</span>
            <ChevronDown size={14} />
          </button>
          {menuOpen && (
            <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', minWidth: 200, background: 'var(--erp-surface)', border: '1px solid var(--erp-border)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.08)', zIndex: 100, padding: 4 }} role="menu">
              <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--erp-border)', fontSize: 12 }}>
                <div style={{ fontWeight: 600 }}>{user?.full_name || 'User'}</div>
                <div style={{ color: 'var(--erp-text-muted)' }}>{user?.email}</div>
                <div style={{ color: 'var(--erp-text-faint)', fontSize: 11, marginTop: 2 }}>{user?.role}</div>
              </div>
              <button className="erp-sidebar-item" style={{ width: '100%', textAlign: 'left' }} onClick={() => { navigate('/profile'); setMenuOpen(false); }}><UserIcon size={14} /> My Profile</button>
              <button className="erp-sidebar-item" style={{ width: '100%', textAlign: 'left' }} onClick={() => { navigate('/settings'); setMenuOpen(false); }}><Settings size={14} /> Settings</button>
              <div style={{ height: 1, background: 'var(--erp-border)', margin: '4px 0' }} />
              <button className="erp-sidebar-item" style={{ width: '100%', textAlign: 'left', color: 'var(--erp-danger)' }} onClick={logout}><LogOut size={14} /> Logout</button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
